import api from '../Api';
import {IActionResponse} from '../../dal/BaseDAL';

/**
 * A link from an Adaptive Catalog object to a related external (CRM/PSA) record. Plain reference data —
 * the UI decides what actions to offer (e.g. "Open in {folioName}"). The live deep-link URL is fetched
 * on demand via resolveRecordUrl.
 */
export interface IRecordLink {
    folioId: string;
    folioName?: string;
    recordType: string;
    recordId: string;
    supported: boolean;
    /** For opportunity links: whether this CRM supports converting the opportunity to a sales order. */
    canConvertToSalesOrder?: boolean;
}

/** Turn a CRM record type ("PurchaseOrder") into prose for labels/tooltips ("purchase order"). */
export const humanizeRecordType = (recordType: string): string =>
    recordType.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();

/** One workspace linked to an external CRM record (e.g. an already-imported opportunity). */
export interface IWorkspaceRecordLink {
    externalId: string;
    workspaceId: string;
    workspaceName?: string;
}

/** Response from a folio's quote-link setup endpoints (permission check and apply). */
export interface IQuoteLinkSetupResponse {
    success: boolean;
    error?: string;
    /** Permission check: whether the connected member may write the quote-link setup table. */
    allowed: boolean;
    id?: string;
    /** Apply: true when an identical link already existed and nothing was created. */
    alreadyExisted?: boolean;
}

export interface IResolveRecordUrlRequest {
    folioId: string;
    recordType: string;
    recordId: string;
}

export interface IResolveRecordUrlResponse {
    success: boolean;
    error?: string;
    url?: string;
    supported: boolean;
}

/** Result of converting a storefront order's CRM opportunity into a sales order. */
export interface IConvertToSalesOrderResponse {
    success: boolean;
    error?: string;
    salesOrderId?: string;
}

/**
 * One installed folio (CRM) a storefront-order opportunity can be created in on demand — any folio with a
 * storefrontOpportunity sub-extension. `alreadyExists` is true once an opportunity already exists for this folio
 * on the order's workspace, in which case creation should not be offered for it.
 */
export interface IOpportunityCreationOption {
    folioId: string;
    folioName?: string;
    extensionId: string;
    alreadyExists: boolean;
}

/** Result of creating a CRM opportunity from a storefront order. */
export interface ICreateOpportunityResponse {
    success: boolean;
    error?: string;
    opportunityId?: string;
    folioName?: string;
}

/** Result of a live "Import All" CRM sync run. */
export interface ICrmSyncResult {
    success: boolean;
    companies: number;
    contacts: number;
    locations: number;
    created: number;
    updated: number;
    skipped: number;
    errors?: string[];
}

/** An installed CRM/PSA folio that supports mapping an Adaptive Catalog vendor to one of its vendor records. */
export interface IVendorMappableFolio {
    folioId: string;
    folioName?: string;
}

/** An installed CRM/PSA folio a company can be manually linked to (its contactImport sub-extension can search companies). */
export interface ICompanyLinkableFolio {
    folioId: string;
    folioName?: string;
}

/** A CRM company candidate returned by the manual "link to CRM company" search. */
export interface ICrmImportCompany {
    externalId?: string;
    name?: string;
    identifier?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    city?: string;
    stateTerritory?: string;
    postalCodeZip?: string;
    country?: string;
    phone?: string;
    website?: string;
    /** Server-computed: true when an AC company is already linked to this CRM company for the folio. */
    alreadyImported?: boolean;
}

export interface ICrmCompanySearchResponse {
    success: boolean;
    error?: string;
    companies?: ICrmImportCompany[];
    hasMore?: boolean;
}

/** A selectable option returned by an extension lookup (e.g. a CRM vendor company). */
export interface IExtensionOption {
    value: string;
    display: string;
}

export interface IExtensionOptionsResponse {
    success: boolean;
    error?: string;
    options?: IExtensionOption[];
}

const recordLinksEndpoints = api.injectEndpoints({
    endpoints: (build) => ({
        getStorefrontOrderLinks: build.query<IRecordLink[], string>({
            query: (orderId) => `storefront/orders/${orderId}/links`
        }),
        getContactLinks: build.query<IRecordLink[], string>({
            query: (contactId) => `contacts/contacts/${contactId}/links`
        }),
        getCompanyLinks: build.query<IRecordLink[], string>({
            query: (companyId) => `contacts/companies/${companyId}/links`,
            providesTags: (_r, _e, companyId) => [{type: 'Companies', id: companyId}]
        }),
        getLocationLinks: build.query<IRecordLink[], string>({
            query: (locationId) => `contacts/locations/${locationId}/links`
        }),
        getDemandItemLinks: build.query<IRecordLink[], string>({
            query: (demandId) => `procurement/demands/items/${demandId}/links`
        }),
        getWorkspaceLinks: build.query<IRecordLink[], string>({
            query: (workspaceId) => `workspaces/${workspaceId}/links`
        }),
        // Detach one CRM record from a workspace; the imported items stay. Callers refetch getWorkspaceLinks.
        deleteWorkspaceLink: build.mutation<IActionResponse, {workspaceId: string; folioId: string; recordId: string}>({
            query: (arg) => ({
                url: `workspaces/${arg.workspaceId}/links/${encodeURIComponent(arg.folioId)}/${encodeURIComponent(arg.recordId)}`,
                method: 'DELETE'
            })
        }),
        getWorkspaceRecordLinks: build.query<IWorkspaceRecordLink[], {field?: string; externalIds: string[]}>({
            query: (body) => ({
                url: 'workspaces/recordLinks',
                method: 'POST',
                body
            })
        }),
        getFolioQuoteLinkPermission: build.query<IQuoteLinkSetupResponse, string>({
            query: (folioId) => `extensions/folios/${encodeURIComponent(folioId)}/quoteLink/permission`
        }),
        applyFolioQuoteLink: build.mutation<IQuoteLinkSetupResponse, {folioId: string; link: string}>({
            query: ({folioId, link}) => ({
                url: `extensions/folios/${encodeURIComponent(folioId)}/quoteLink/apply`,
                method: 'POST',
                body: {link}
            })
        }),
        resolveRecordUrl: build.query<IResolveRecordUrlResponse, IResolveRecordUrlRequest>({
            query: ({folioId, recordType, recordId}) =>
                `extensions/folios/${encodeURIComponent(folioId)}/links/${encodeURIComponent(recordType)}/${encodeURIComponent(recordId)}`
        }),
        convertStorefrontOrderToSalesOrder: build.mutation<IConvertToSalesOrderResponse, {orderId: string; folioId: string}>({
            query: ({orderId, folioId}) => ({
                url: `storefront/orders/${orderId}/convertToSalesOrder/${encodeURIComponent(folioId)}`,
                method: 'POST'
            })
        }),
        getStorefrontOrderOpportunityCreationOptions: build.query<IOpportunityCreationOption[], string>({
            query: (orderId) => `storefront/orders/${orderId}/opportunityCreation`
        }),
        createStorefrontOrderOpportunity: build.mutation<ICreateOpportunityResponse, {orderId: string; folioId: string}>({
            query: ({orderId, folioId}) => ({
                url: `storefront/orders/${orderId}/createOpportunity/${encodeURIComponent(folioId)}`,
                method: 'POST'
            })
        }),
        getVendorMappableFolios: build.query<IVendorMappableFolio[], void>({
            query: () => 'extensions/folios/vendorMappable'
        }),
        getCompanyLinkableFolios: build.query<ICompanyLinkableFolio[], void>({
            query: () => 'extensions/folios/companyLinkable'
        }),
        searchFolioCompanies: build.query<ICrmCompanySearchResponse, {folioId: string; search?: string}>({
            query: ({folioId, search}) =>
                `extensions/folios/${encodeURIComponent(folioId)}/companies/search${search ? `?search=${encodeURIComponent(search)}` : ''}`
        }),
        runCrmFolioSync: build.mutation<ICrmSyncResult, string>({
            query: (folioId) => ({
                url: `extensions/folios/${encodeURIComponent(folioId)}/sync`,
                method: 'POST'
            })
        }),
        linkCompanyToFolio: build.mutation<IActionResponse, {companyId: string; folioId: string; externalId: string}>({
            query: ({companyId, folioId, externalId}) => ({
                url: `contacts/companies/${encodeURIComponent(companyId)}/link/${encodeURIComponent(folioId)}`,
                method: 'PATCH',
                body: {externalId}
            }),
            invalidatesTags: (_r, _e, {companyId}) => [{type: 'Companies', id: companyId}]
        }),
        importCompanyLocations: build.mutation<ICrmSyncResult, {companyId: string; folioId: string}>({
            query: ({companyId, folioId}) => ({
                url: `contacts/companies/${encodeURIComponent(companyId)}/importLocations/${encodeURIComponent(folioId)}`,
                method: 'POST'
            }),
            invalidatesTags: (_r, _e, {companyId}) => [{type: 'Locations', id: companyId}, {type: 'Companies', id: companyId}]
        }),
        importCompanyContacts: build.mutation<ICrmSyncResult, {companyId: string; folioId: string}>({
            query: ({companyId, folioId}) => ({
                url: `contacts/companies/${encodeURIComponent(companyId)}/importContacts/${encodeURIComponent(folioId)}`,
                method: 'POST'
            }),
            invalidatesTags: (_r, _e, {companyId}) => [{type: 'Contacts', id: companyId}]
        }),
        importCrmCompany: build.mutation<ICrmSyncResult, {folioId: string; externalId: string; importContacts: boolean; importLocations: boolean}>({
            query: ({folioId, externalId, importContacts, importLocations}) => ({
                url: `extensions/folios/${encodeURIComponent(folioId)}/companies/import/${encodeURIComponent(externalId)}?importContacts=${importContacts}&importLocations=${importLocations}`,
                method: 'POST'
            })
        }),
        searchFolioVendors: build.query<IExtensionOptionsResponse, {folioId: string; search?: string}>({
            query: ({folioId, search}) =>
                `extensions/folios/${encodeURIComponent(folioId)}/vendors${search ? `?search=${encodeURIComponent(search)}` : ''}`
        }),
        getFolioVendorFriendlyName: build.query<string, {folioId: string; externalId: string}>({
            query: ({folioId, externalId}) =>
                `extensions/folios/${encodeURIComponent(folioId)}/vendors/${encodeURIComponent(externalId)}/friendlyName`
        })
    })
});

export const {
    useDeleteWorkspaceLinkMutation,
    useGetStorefrontOrderLinksQuery,
    useGetContactLinksQuery,
    useGetCompanyLinksQuery,
    useGetLocationLinksQuery,
    useGetDemandItemLinksQuery,
    useGetWorkspaceLinksQuery,
    useLazyGetWorkspaceRecordLinksQuery,
    useLazyResolveRecordUrlQuery,
    useGetFolioQuoteLinkPermissionQuery,
    useApplyFolioQuoteLinkMutation,
    useConvertStorefrontOrderToSalesOrderMutation,
    useGetStorefrontOrderOpportunityCreationOptionsQuery,
    useCreateStorefrontOrderOpportunityMutation,
    useGetVendorMappableFoliosQuery,
    useGetCompanyLinkableFoliosQuery,
    useSearchFolioCompaniesQuery,
    useLazySearchFolioCompaniesQuery,
    useRunCrmFolioSyncMutation,
    useLinkCompanyToFolioMutation,
    useImportCompanyLocationsMutation,
    useImportCompanyContactsMutation,
    useImportCrmCompanyMutation,
    useSearchFolioVendorsQuery,
    useLazySearchFolioVendorsQuery,
    useGetFolioVendorFriendlyNameQuery
} = recordLinksEndpoints;
